import { normalizeBible } from './connectedBible'
import type {
  NodeKind,
  StoryBlock,
  StoryChoice,
  StoryEdge,
  StoryNode,
  StoryProject,
  DraftStatus,
  StoryBible,
  StoryCondition,
  StoryEffect,
  StoryValue,
  StoryVariable,
} from './types'
import { storyLogicIssues, valueMatchesType } from './storyLogic'

const words = (value: string) =>
  value.trim().match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu) ?? []

export function wordCount(value: string) {
  return words(value).length
}

export function nodeWordCount(node: StoryNode) {
  return (
    node.blocks.reduce((total, block) => total + wordCount(block.text), 0) +
    node.choices.reduce((total, choice) => total + wordCount(choice.label), 0)
  )
}

export function storyWordCount(project: StoryProject) {
  return project.nodes.reduce((total, node) => total + nodeWordCount(node), 0)
}

export function readingMinutes(project: StoryProject) {
  return Math.max(1, Math.ceil(storyWordCount(project) / 180))
}

export function slugify(value: string, fallback = 'scene') {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || fallback
}

export function uniqueId(base: string, usedIds: Iterable<string>) {
  const used = new Set(usedIds)
  const clean = slugify(base)
  if (!used.has(clean)) return clean
  let index = 2
  while (used.has(`${clean}-${index}`)) index += 1
  return `${clean}-${index}`
}

const uid = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

export function createBlock(kind: StoryBlock['kind']): StoryBlock {
  if (kind === 'dialogue') {
    return {
      id: uid('block'),
      kind,
      speaker: 'CHARACTER',
      direction: '',
      text: '',
    }
  }
  return { id: uid('block'), kind, text: '' }
}

export function createChoice(): StoryChoice {
  return { id: uid('choice'), label: '', targetId: null }
}

export const draftStatuses: Array<{ value: DraftStatus; label: string }> = [
  { value: 'idea', label: 'Idea' },
  { value: 'draft', label: 'In draft' },
  { value: 'review', label: 'To revise' },
  { value: 'final', label: 'Final' },
]

export const emptyBible = (): StoryBible => ({
  premise: '',
  world: '',
  notes: '',
  characters: [],
})

export function addPlayerChoice(
  project: StoryProject,
  nodeId: string,
): StoryProject {
  return {
    ...project,
    nodes: project.nodes.map((node) => {
      if (node.id !== nodeId || node.kind === 'ending') return node
      const choices = [...node.choices]
      // Keep the existing route when a linear scene becomes a decision.
      if (!choices.length && node.nextId) {
        const target = project.nodes.find(
          (candidate) => candidate.id === node.nextId,
        )
        choices.push({
          ...createChoice(),
          label: `Continue to ${target?.title || 'next scene'}`,
          targetId: node.nextId,
        })
      }
      return { ...node, nextId: null, choices: [...choices, createChoice()] }
    }),
  }
}

export function duplicateNode(
  project: StoryProject,
  nodeId: string,
): StoryProject {
  const original = project.nodes.find((node) => node.id === nodeId)
  if (!original) return project
  const copy: StoryNode = {
    ...original,
    id: uniqueId(
      `${original.id}-copy`,
      project.nodes.map((node) => node.id),
    ),
    title: `${original.title || 'Untitled scene'} (copy)`,
    status: 'draft',
    position: { x: original.position.x + 50, y: original.position.y + 320 },
    blocks: original.blocks.map((block) => ({ ...block, id: uid('block') })),
    choices: original.choices.map((choice) => ({
      ...choice,
      id: uid('choice'),
      ...(choice.conditions
        ? {
            conditions: choice.conditions.map((condition) => ({
              ...condition,
              id: uid('condition'),
            })),
          }
        : {}),
      ...(choice.effects
        ? {
            effects: choice.effects.map((effect) => ({
              ...effect,
              id: uid('effect'),
            })),
          }
        : {}),
    })),
    ...(original.effects
      ? {
          effects: original.effects.map((effect) => ({
            ...effect,
            id: uid('effect'),
          })),
        }
      : {}),
  }
  const index = project.nodes.indexOf(original)
  return {
    ...project,
    nodes: [
      ...project.nodes.slice(0, index + 1),
      copy,
      ...project.nodes.slice(index + 1),
    ],
  }
}

export function sceneSearchText(node: StoryNode) {
  return [
    node.title,
    node.id,
    node.chapter,
    node.synopsis,
    node.notes,
    ...node.blocks.map(
      (block) =>
        `${block.speaker ?? ''} ${block.direction ?? ''} ${block.text}`,
    ),
    ...node.choices.map((choice) => choice.label),
  ]
    .join(' ')
    .toLowerCase()
}

export function createNode(
  project: StoryProject,
  position: { x: number; y: number },
  title = 'New scene',
): StoryNode {
  const titles = new Set(project.nodes.map((node) => node.title.toLowerCase()))
  let sceneTitle = title
  let suffix = 2
  while (titles.has(sceneTitle.toLowerCase()))
    sceneTitle = title + ' ' + suffix++
  return {
    id: uniqueId(
      sceneTitle,
      project.nodes.map((node) => node.id),
    ),
    title: sceneTitle,
    kind: 'scene',
    position,
    blocks: [createBlock('narration')],
    choices: [],
    nextId: null,
    status: 'draft',
  }
}

export function deriveEdges(project: StoryProject): StoryEdge[] {
  const validIds = new Set(project.nodes.map((node) => node.id))
  const edges: StoryEdge[] = []
  for (const node of project.nodes) {
    if (node.kind === 'ending') continue
    if (!node.choices.length && node.nextId && validIds.has(node.nextId)) {
      edges.push({
        id: `continue:${node.id}`,
        sourceId: node.id,
        targetId: node.nextId,
        kind: 'continue',
      })
    }
    for (const choice of node.choices) {
      if (!choice.targetId || !validIds.has(choice.targetId)) continue
      edges.push({
        id: `choice:${node.id}:${choice.id}`,
        sourceId: node.id,
        targetId: choice.targetId,
        kind: 'choice',
        choiceId: choice.id,
        label: choice.label,
      })
    }
  }
  return edges
}

export function reachableNodeIds(project: StoryProject) {
  const edges = deriveEdges(project)
  const outgoing = new Map<string, string[]>()
  for (const edge of edges)
    outgoing.set(edge.sourceId, [
      ...(outgoing.get(edge.sourceId) ?? []),
      edge.targetId,
    ])
  const reachable = new Set<string>()
  const pending = [project.startNodeId]
  while (pending.length) {
    const id = pending.shift()
    if (!id || reachable.has(id)) continue
    reachable.add(id)
    pending.push(...(outgoing.get(id) ?? []))
  }
  return reachable
}

export type StoryIssue = {
  nodeId: string
  message: string
  kind: 'flow' | 'draft'
  severity: 'warning' | 'note'
}

export function projectIssues(project: StoryProject): StoryIssue[] {
  const ids = new Set(project.nodes.map((node) => node.id))
  const reachable = reachableNodeIds(project)
  const edges = deriveEdges(project)
  const incoming = new Map<string, string[]>()
  const sources = new Set(edges.map((edge) => edge.sourceId))
  for (const edge of edges)
    incoming.set(edge.targetId, [
      ...(incoming.get(edge.targetId) ?? []),
      edge.sourceId,
    ])
  const endingIds = project.nodes
    .filter((node) => node.kind === 'ending')
    .map((node) => node.id)
  const reachesEnding = new Set<string>()
  const pending = [...endingIds]
  while (pending.length) {
    const id = pending.pop()!
    if (reachesEnding.has(id)) continue
    reachesEnding.add(id)
    pending.push(...(incoming.get(id) ?? []))
  }
  const issues: StoryIssue[] = []
  const flow = (nodeId: string, message: string) =>
    issues.push({ nodeId, message, kind: 'flow', severity: 'warning' })
  const draft = (nodeId: string, message: string) =>
    issues.push({ nodeId, message, kind: 'draft', severity: 'note' })
  if (!endingIds.length)
    draft(project.startNodeId, 'No ending has been marked yet.')
  for (const node of project.nodes) {
    if (!reachable.has(node.id))
      flow(node.id, 'This scene is not reachable from the opening.')
    if (
      endingIds.length &&
      reachable.has(node.id) &&
      sources.has(node.id) &&
      !reachesEnding.has(node.id)
    )
      flow(node.id, 'No connected route from this scene leads to an ending.')
    if (!node.blocks.some((block) => block.text.trim()))
      draft(node.id, 'This scene is waiting for its first line.')
    if (node.status === 'review')
      draft(node.id, 'You marked this scene for revision.')
    if (
      node.blocks.some(
        (block) =>
          block.kind === 'dialogue' &&
          (!block.speaker?.trim() || block.speaker === 'CHARACTER'),
      )
    ) {
      draft(node.id, 'Give every dialogue line a character name.')
    }
    if (node.kind === 'ending') {
      if (node.nextId || node.choices.length)
        flow(
          node.id,
          'This ending still has outgoing routes. Convert it to a scene to use them.',
        )
      continue
    }
    if (node.kind === 'scene' && !node.nextId && node.choices.length === 0) {
      flow(
        node.id,
        'Choose what happens next, or mark this scene as an ending.',
      )
    }
    if (node.nextId && !ids.has(node.nextId))
      flow(node.id, 'Its continuation points to a missing scene.')
    if (node.nextId && node.choices.length)
      flow(
        node.id,
        'Player choices take priority over this scene’s continuation.',
      )
    for (const choice of node.choices) {
      if (!choice.label.trim())
        draft(node.id, 'Write the text for an untitled player choice.')
      if (!choice.targetId)
        flow(
          node.id,
          `“${choice.label || 'Untitled choice'}” needs a destination.`,
        )
      else if (!ids.has(choice.targetId))
        flow(node.id, `“${choice.label}” points to a missing scene.`)
    }
  }
  for (const issue of storyLogicIssues(project))
    flow(issue.nodeId, issue.message)
  return issues
}

export function renameNode(
  project: StoryProject,
  nodeId: string,
  requestedId: string,
): StoryProject {
  const nextId = uniqueId(
    requestedId,
    project.nodes.filter((node) => node.id !== nodeId).map((node) => node.id),
  )
  if (nextId === nodeId) return project
  return {
    ...project,
    startNodeId: project.startNodeId === nodeId ? nextId : project.startNodeId,
    ...(project.bible
      ? {
          bible: {
            ...project.bible,
            ...(project.bible.links ? { links: project.bible.links.map((link) => ({
              ...link,
              sourceId:
                link.sourceId === 'scene:' + nodeId
                  ? 'scene:' + nextId
                  : link.sourceId,
              targetId:
                link.targetId === 'scene:' + nodeId
                  ? 'scene:' + nextId
                  : link.targetId,
            })) } : {}),
            ...(project.bible.parents ? { parents: project.bible.parents.map(p => ({
              childId: p.childId === 'scene:' + nodeId ? 'scene:' + nextId : p.childId,
              parentId: p.parentId === 'scene:' + nodeId ? 'scene:' + nextId : p.parentId,
            })) } : {}),
          },
        }
      : {}),
    nodes: project.nodes.map((node) => ({
      ...node,
      id: node.id === nodeId ? nextId : node.id,
      nextId: node.nextId === nodeId ? nextId : node.nextId,
      choices: node.choices.map((choice) => ({
        ...choice,
        targetId: choice.targetId === nodeId ? nextId : choice.targetId,
      })),
    })),
  }
}

export function removeNode(
  project: StoryProject,
  nodeId: string,
): StoryProject {
  if (project.nodes.length <= 1) return project
  const nodes = project.nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      nextId: node.nextId === nodeId ? null : node.nextId,
      choices: node.choices.map((choice) => ({
        ...choice,
        targetId: choice.targetId === nodeId ? null : choice.targetId,
      })),
    }))
  return {
    ...project,
    nodes,
    startNodeId:
      project.startNodeId === nodeId ? nodes[0].id : project.startNodeId,
    ...(project.bible
      ? {
          bible: {
            ...project.bible,
            ...(project.bible.links ? { links: project.bible.links.filter(
              (link) =>
                link.sourceId !== 'scene:' + nodeId &&
                link.targetId !== 'scene:' + nodeId,
            ) } : {}),
            ...(project.bible.parents ? { parents: project.bible.parents.filter(p =>
              p.childId !== 'scene:' + nodeId && p.parentId !== 'scene:' + nodeId,
            ) } : {}),
          },
        }
      : {}),
  }
}

export function disconnectEdge(
  project: StoryProject,
  edgeId: string,
): StoryProject {
  const [kind, sourceId, choiceId] = edgeId.split(':')
  return {
    ...project,
    nodes: project.nodes.map((node) => {
      if (node.id !== sourceId) return node
      if (kind === 'continue') return { ...node, nextId: null }
      if (kind === 'choice') {
        return {
          ...node,
          choices: node.choices.map((choice) =>
            choice.id === choiceId ? { ...choice, targetId: null } : choice,
          ),
        }
      }
      return node
    }),
  }
}

export function removeStoryElements(
  project: StoryProject,
  nodeIds: string[],
  edgeIds: string[],
): StoryProject {
  let next = project
  for (const id of new Set(nodeIds)) next = removeNode(next, id)
  for (const id of new Set(edgeIds)) next = disconnectEdge(next, id)
  return next
}

export function setNodeKind(node: StoryNode, kind: NodeKind): StoryNode {
  return kind === 'ending'
    ? { ...node, kind, choices: [], nextId: null }
    : { ...node, kind }
}

export function autoLayout(project: StoryProject): StoryProject {
  const edges = deriveEdges(project)
  const outgoing = new Map<string, string[]>()
  for (const edge of edges)
    outgoing.set(edge.sourceId, [
      ...(outgoing.get(edge.sourceId) ?? []),
      edge.targetId,
    ])

  const level = new Map<string, number>([[project.startNodeId, 0]])
  const pending = [project.startNodeId]
  while (pending.length) {
    const id = pending.shift()!
    const currentLevel = level.get(id) ?? 0
    for (const target of outgoing.get(id) ?? []) {
      if (level.has(target)) continue
      level.set(target, currentLevel + 1)
      pending.push(target)
    }
  }

  let disconnectedLevel = Math.max(0, ...level.values()) + 1
  const rows = new Map<number, number>()
  return {
    ...project,
    nodes: project.nodes.map((node) => {
      const column = level.get(node.id) ?? disconnectedLevel++
      const row = rows.get(column) ?? 0
      rows.set(column, row + 1)
      return { ...node, position: { x: 70 + column * 390, y: 70 + row * 260 } }
    }),
  }
}

function text(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback
}

export function normalizeProject(value: unknown): StoryProject {
  if (!value || typeof value !== 'object')
    throw new Error('Project data is invalid.')
  const input = value as Partial<StoryProject>
  const sourceNodes = Array.isArray(input.nodes) ? input.nodes : []
  if (!sourceNodes.length) throw new Error('A story needs at least one scene.')
  const seen = new Set<string>()
  const nodeIdMap = new Map<string, string>()
  const nodeIds = sourceNodes.map((raw, index) => {
    if (!raw || typeof raw !== 'object')
      throw new Error(`Scene ${index + 1} is invalid.`)
    const original = text(raw.id, `scene-${index + 1}`)
    const id = uniqueId(original, seen)
    seen.add(id)
    if (!nodeIdMap.has(original)) nodeIdMap.set(original, id)
    return id
  })
  const routeId = (id: unknown) =>
    typeof id === 'string' ? (nodeIdMap.get(id) ?? id) : null
  const identities = new Set<string>()
  const identity = (id: unknown, fallback: string) => {
    const next = uniqueId(text(id, fallback) || fallback, identities)
    identities.add(next)
    return next
  }
  const variableIdMap = new Map<string, string>()
  const variables: StoryVariable[] = Array.isArray(input.variables)
    ? input.variables.map((raw, index) => {
        if (
          !raw ||
          typeof raw !== 'object' ||
          !['boolean', 'number', 'text'].includes(raw.type)
        )
          throw new Error(`Story variable ${index + 1} is invalid.`)
        if (!valueMatchesType(raw.initialValue, raw.type))
          throw new Error(
            `Story variable ${index + 1} has an invalid starting value.`,
          )
        const original = text(raw.id, `variable-${index + 1}`)
        const id = identity(original, `variable-${index + 1}`)
        if (!variableIdMap.has(original)) variableIdMap.set(original, id)
        return {
          id,
          name: text(raw.name),
          type: raw.type,
          initialValue: raw.initialValue,
          description: text(raw.description),
        }
      })
    : []
  const ruleValue = (value: unknown): StoryValue => {
    if (
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value))
    )
      return value
    throw new Error(
      'A story rule has an invalid value. Use text, a number, or yes / no.',
    )
  }
  const effects = (value: unknown): StoryEffect[] => {
    if (!Array.isArray(value)) throw new Error('Story changes must be a list.')
    return value.map((raw, index) => {
      if (
        !raw ||
        typeof raw !== 'object' ||
        !['set', 'add', 'subtract'].includes(raw.operation)
      )
        throw new Error('A story change has an invalid operation.')
      return {
        id: identity(raw.id, `effect-${index + 1}`),
        variableId: variableIdMap.get(raw.variableId) ?? text(raw.variableId),
        operation: raw.operation,
        value: ruleValue(raw.value),
      }
    })
  }
  const conditions = (value: unknown): StoryCondition[] => {
    if (!Array.isArray(value))
      throw new Error('Choice conditions must be a list.')
    return value.map((raw, index) => {
      if (
        !raw ||
        typeof raw !== 'object' ||
        !['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains'].includes(
          raw.operator,
        )
      )
        throw new Error('A choice condition has an invalid comparison.')
      return {
        id: identity(raw.id, `condition-${index + 1}`),
        variableId: variableIdMap.get(raw.variableId) ?? text(raw.variableId),
        operator: raw.operator,
        value: ruleValue(raw.value),
      }
    })
  }
  const nodes = sourceNodes.map((raw, index): StoryNode => {
    if (!raw || typeof raw !== 'object')
      throw new Error(`Scene ${index + 1} is invalid.`)
    const source = raw as Partial<StoryNode>
    const id = nodeIds[index]
    const blocks = Array.isArray(source.blocks)
      ? source.blocks.map((rawBlock, blockIndex): StoryBlock => {
          if (!rawBlock || typeof rawBlock !== 'object')
            throw new Error(`A line in scene ${index + 1} is invalid.`)
          const block = rawBlock as Partial<StoryBlock>
          const kind = block.kind === 'dialogue' ? 'dialogue' : 'narration'
          return {
            id: identity(block.id, `${id}-block-${blockIndex + 1}`),
            kind,
            text: text(block.text),
            ...(kind === 'dialogue'
              ? {
                  speaker: text(block.speaker, 'CHARACTER'),
                  direction: text(block.direction),
                }
              : {}),
          }
        })
      : []
    const choices = Array.isArray(source.choices)
      ? source.choices.map((rawChoice, choiceIndex): StoryChoice => {
          if (!rawChoice || typeof rawChoice !== 'object')
            throw new Error(`A choice in scene ${index + 1} is invalid.`)
          const choice = rawChoice as Partial<StoryChoice>
          return {
            id: identity(choice.id, `${id}-choice-${choiceIndex + 1}`),
            label: text(choice.label, `Choice ${choiceIndex + 1}`),
            targetId: routeId(choice.targetId),
            ...(choice.conditions !== undefined
              ? { conditions: conditions(choice.conditions) }
              : {}),
            ...(choice.effects !== undefined
              ? { effects: effects(choice.effects) }
              : {}),
            ...(choice.conditionMode === 'any' ? { conditionMode: 'any' } : {}),
            ...(choice.unavailableMode === 'hide'
              ? { unavailableMode: 'hide' }
              : {}),
          }
        })
      : []
    const position =
      source.position &&
      Number.isFinite(source.position.x) &&
      Number.isFinite(source.position.y)
        ? { x: source.position.x, y: source.position.y }
        : { x: index * 390, y: 100 }
    return {
      id,
      title: text(source.title, `Scene ${index + 1}`),
      kind: source.kind === 'ending' ? 'ending' : 'scene',
      position,
      blocks,
      choices,
      nextId: routeId(source.nextId),
      ...(source.effects !== undefined
        ? { effects: effects(source.effects) }
        : {}),
      ...(source.status &&
      draftStatuses.some((status) => status.value === source.status)
        ? { status: source.status }
        : {}),
      ...(typeof source.chapter === 'string'
        ? { chapter: source.chapter }
        : {}),
      ...(typeof source.synopsis === 'string'
        ? { synopsis: source.synopsis }
        : {}),
      ...(typeof source.notes === 'string' ? { notes: source.notes } : {}),
    }
  })
  const idSet = new Set(nodes.map((node) => node.id))
  return {
    version: 1,
    id: slugify(text(input.id, input.title ?? 'story'), 'story'),
    title: text(input.title, 'Untitled Story'),
    startNodeId: idSet.has(routeId(input.startNodeId) ?? '')
      ? routeId(input.startNodeId)!
      : nodes[0].id,
    nodes,
    ...(input.variables !== undefined ? { variables } : {}),
    ...(input.bible && typeof input.bible === 'object'
      ? {
          bible: normalizeBible(input.bible, nodeIdMap),
        }
      : {}),
    ...(typeof input.wordGoal === 'number' &&
    Number.isFinite(input.wordGoal) &&
    input.wordGoal >= 0
      ? { wordGoal: Math.floor(input.wordGoal) }
      : {}),
  }
}
